// /Frontend/src/Pages/Signup.jsx
import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { apiCall } from '../config/api';
import {
  Lock, Mail, User, Building2, Eye, EyeOff,
  CheckCircle, AlertCircle, ArrowLeft, Clock
} from 'lucide-react';

const Signup = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    firstName: '', lastName: '', email: '',
    company: '', password: '', confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState([]);
  const [fieldErrors, setFieldErrors] = useState({});

  const [pendingState, setPendingState] = useState(null); // { companyName } when signup returns 202

  // Company is a plain text field — no autocomplete, no dropdown of existing
  // company names (that would let anyone enumerate the customer list just by
  // typing letters). Whether the company exists is checked once, server-side,
  // when the form is submitted. No hint is shown while typing; if the name
  // doesn't match an existing company, the submit fails with a plain error.

  const validateForm = () => {
    const newErrors = [];
    const newFieldErrors = {};

    if (!formData.firstName.trim() || formData.firstName.trim().length < 2) {
      newFieldErrors.firstName = 'First name must be at least 2 characters';
      newErrors.push('First name is required');
    }
    if (!formData.lastName.trim() || formData.lastName.trim().length < 2) {
      newFieldErrors.lastName = 'Last name must be at least 2 characters';
      newErrors.push('Last name is required');
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email || !emailRegex.test(formData.email)) {
      newFieldErrors.email = 'Please enter a valid email address';
      newErrors.push('Valid email is required');
    }
    if (!formData.company.trim() || formData.company.trim().length < 2) {
      newFieldErrors.company = 'Company name is required';
      newErrors.push('Company name is required');
    }
    if (!formData.password || formData.password.length < 8) {
      newFieldErrors.password = 'Password must be at least 8 characters';
      newErrors.push('Password must be at least 8 characters');
    } else {
      if (!/[A-Z]/.test(formData.password) || !/[a-z]/.test(formData.password) || !/[0-9]/.test(formData.password)) {
        newFieldErrors.password = 'Password must contain uppercase, lowercase, and number';
        newErrors.push('Password complexity not met');
      }
    }
    if (formData.password !== formData.confirmPassword) {
      newFieldErrors.confirmPassword = 'Passwords do not match';
      newErrors.push('Passwords do not match');
    }

    setFieldErrors(newFieldErrors);
    setErrors(newErrors);
    return newErrors.length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    setErrors([]);

    try {
      const response = await apiCall('/auth/signup', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.status === 202 && data.pending) {
        // Matched an existing company → pending admin approval
        setPendingState({ companyName: data.companyName });
      } else if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('user', JSON.stringify(data.user));
        window.location.href = '/dashboard';
      } else {
        // Includes the case where the company name doesn't match any
        // existing company — the backend returns a plain validation error
        // rather than the frontend guessing at existence.
        if (data.errors && Array.isArray(data.errors)) {
          setErrors(data.errors);
          const companyError = data.errors.find(e => e.toLowerCase().includes('company'));
          if (companyError) {
            setFieldErrors(prev => ({ ...prev, company: companyError }));
          }
        } else if (data.message) {
          setErrors([data.message]);
        } else {
          setErrors(['Signup failed. Please try again.']);
        }
      }
    } catch (error) {
      console.error('Signup error:', error);
      setErrors(['An error occurred during signup. Please try again.']);
    } finally {
      setLoading(false);
    }
  };

  const getPasswordStrength = () => {
    const p = formData.password;
    if (!p) return { strength: 0, label: '', color: '' };
    let s = 0;
    if (p.length >= 8) s++;
    if (p.length >= 12) s++;
    if (/[A-Z]/.test(p)) s++;
    if (/[a-z]/.test(p)) s++;
    if (/[0-9]/.test(p)) s++;
    if (/[^A-Za-z0-9]/.test(p)) s++;
    if (s <= 2) return { strength: 33, label: 'Weak', color: 'bg-red-500' };
    if (s <= 4) return { strength: 66, label: 'Medium', color: 'bg-yellow-500' };
    return { strength: 100, label: 'Strong', color: 'bg-green-500' };
  };

  const passwordStrength = getPasswordStrength();

  // ── Pending approval screen ──────────────────────────────────────────────
  if (pendingState) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4">
        <div className="w-full max-w-md text-center">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-yellow-500 to-orange-400 rounded-2xl mb-6">
            <Clock className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-3">Pending Approval</h1>
          <p className="text-slate-400 mb-6">
            Your account has been created and is awaiting approval from the{' '}
            <span className="text-yellow-400 font-medium">{pendingState.companyName}</span> administrator.
          </p>
          <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-6 mb-6">
            <div className="flex items-start space-x-3 text-left">
              <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
              <p className="text-slate-300 text-sm">Account created successfully</p>
            </div>
            <div className="flex items-start space-x-3 text-left mt-3">
              <Clock className="w-5 h-5 text-yellow-400 flex-shrink-0 mt-0.5" />
              <p className="text-slate-300 text-sm">Waiting for admin to approve your access</p>
            </div>
            <div className="flex items-start space-x-3 text-left mt-3">
              <Mail className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
              <p className="text-slate-500 text-sm">You'll be able to log in once approved</p>
            </div>
          </div>
          <Link
            to="/admin/login"
            className="inline-flex items-center space-x-2 text-blue-400 hover:text-blue-300 transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span>Back to Sign In</span>
          </Link>
        </div>
      </div>
    );
  }

  // ── Main signup form ─────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-blue-500 to-cyan-400 rounded-2xl mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-white mb-2">Create Account</h1>
          <p className="text-slate-400">Join KaydSystems</p>
        </div>

        <div className="bg-slate-900/50 border border-slate-800 rounded-2xl p-8 backdrop-blur-lg">
          {errors.length > 0 && (
            <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-lg">
              <div className="flex items-start space-x-2">
                <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  {errors.map((error, index) => (
                    <p key={index} className="text-red-400 text-sm">{error}</p>
                  ))}
                </div>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Name Row */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">First Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text" name="firstName" value={formData.firstName} onChange={handleChange}
                    className={`w-full bg-slate-800/50 border ${fieldErrors.firstName ? 'border-red-500/50' : 'border-slate-700'} rounded-lg px-10 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors`}
                    placeholder="First" required
                  />
                </div>
                {fieldErrors.firstName && <p className="text-red-400 text-xs mt-1">{fieldErrors.firstName}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">Last Name</label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                  <input
                    type="text" name="lastName" value={formData.lastName} onChange={handleChange}
                    className={`w-full bg-slate-800/50 border ${fieldErrors.lastName ? 'border-red-500/50' : 'border-slate-700'} rounded-lg px-10 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors`}
                    placeholder="Last" required
                  />
                </div>
                {fieldErrors.lastName && <p className="text-red-400 text-xs mt-1">{fieldErrors.lastName}</p>}
              </div>
            </div>

            {/* Email — any domain accepted, no matching logic tied to it */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Email Address</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="email" name="email" value={formData.email} onChange={handleChange}
                  className={`w-full bg-slate-800/50 border ${fieldErrors.email ? 'border-red-500/50' : 'border-slate-700'} rounded-lg px-10 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors`}
                  placeholder="you@example.com" required
                />
              </div>
              {fieldErrors.email && <p className="text-red-400 text-xs mt-1">{fieldErrors.email}</p>}
            </div>

            {/* Company — plain text field, no autocomplete/dropdown. Existence
                is validated server-side on submit only. */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Company</label>
              <div className="relative">
                <Building2 className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type="text" name="company" value={formData.company} onChange={handleChange}
                  autoComplete="off"
                  className={`w-full bg-slate-800/50 border ${fieldErrors.company ? 'border-red-500/50' : 'border-slate-700'} rounded-lg px-10 py-3 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors`}
                  placeholder="Your company name" required
                />
              </div>
              {fieldErrors.company && <p className="text-red-400 text-xs mt-1">{fieldErrors.company}</p>}
            </div>

            {/* Password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showPassword ? 'text' : 'password'} name="password" value={formData.password} onChange={handleChange}
                  className={`w-full bg-slate-800/50 border ${fieldErrors.password ? 'border-red-500/50' : 'border-slate-700'} rounded-lg px-10 py-3 pr-12 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors`}
                  placeholder="••••••••" required
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors">
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.password && (
                <div className="mt-2">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-400">Password strength:</span>
                    <span className={`text-xs font-medium ${passwordStrength.label === 'Strong' ? 'text-green-400' : passwordStrength.label === 'Medium' ? 'text-yellow-400' : 'text-red-400'}`}>
                      {passwordStrength.label}
                    </span>
                  </div>
                  <div className="w-full bg-slate-800 rounded-full h-1.5 overflow-hidden">
                    <div className={`h-full ${passwordStrength.color} transition-all duration-300`} style={{ width: `${passwordStrength.strength}%` }}></div>
                  </div>
                </div>
              )}
              {fieldErrors.password && <p className="text-red-400 text-xs mt-1">{fieldErrors.password}</p>}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-sm font-medium text-slate-300 mb-2">Confirm Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-slate-400" />
                <input
                  type={showConfirmPassword ? 'text' : 'password'} name="confirmPassword" value={formData.confirmPassword} onChange={handleChange}
                  className={`w-full bg-slate-800/50 border ${fieldErrors.confirmPassword ? 'border-red-500/50' : 'border-slate-700'} rounded-lg px-10 py-3 pr-12 text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 transition-colors`}
                  placeholder="••••••••" required
                />
                <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white transition-colors">
                  {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {formData.confirmPassword && formData.password === formData.confirmPassword && (
                <div className="flex items-center space-x-1 mt-1">
                  <CheckCircle className="w-4 h-4 text-green-400" />
                  <span className="text-green-400 text-xs">Passwords match</span>
                </div>
              )}
              {fieldErrors.confirmPassword && <p className="text-red-400 text-xs mt-1">{fieldErrors.confirmPassword}</p>}
            </div>

            {/* Submit */}
            <button type="submit" disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-cyan-400 hover:from-blue-600 hover:to-cyan-500 text-white font-medium py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2">
              {loading ? (
                <>
                  <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  <span>Creating Account...</span>
                </>
              ) : (
                <span>Request Access</span>
              )}
            </button>
          </form>

          <div className="mt-6 pt-6 border-t border-slate-800">
            <p className="text-center text-slate-400">
              Already have an account?{' '}
              <Link to="/admin/login" className="text-blue-400 hover:text-blue-300 font-medium transition-colors inline-flex items-center space-x-1">
                <ArrowLeft className="w-4 h-4" />
                <span>Sign In</span>
              </Link>
            </p>
          </div>
        </div>

        <p className="text-center text-slate-500 text-sm mt-6">
          By signing up, you agree to our Terms of Service and Privacy Policy
        </p>
      </div>
    </div>
  );
};

export default Signup;